import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static get targets() {
    return [
      "emptyState",
      "hidden",
      "item",
      "list",
      "multiplierInput",
      "singleInput",
      "template"
    ]
  }

  static get values() {
    return {
      mode: String,
      modifierType: String
    }
  }

  connect() {
    if (this.modeValue === "list") {
      this.syncListUi()
    }

    if (this.modeValue === "modifier" && this.hasMultiplierInputTarget) {
      const fieldValue = this.readHiddenValues()[0] || {}
      const modifier = this.normalizeModifier(
        fieldValue.modifier,
        this.modifierTypeValue
      )

      this.multiplierInputTarget.value = this.displayFromCents(
        modifier.modifier_value
      )
    }
  }

  addValue() {
    if (!this.hasListTarget || !this.hasTemplateTarget) {
      return
    }

    this.listTarget.appendChild(this.templateTarget.content.cloneNode(true))
    this.syncListUi()
    this.syncListHiddenField()

    const newItem = this.itemTargets[this.itemTargets.length - 1]
    const input = this.findElementByRole(newItem, "label-input")

    if (input) {
      input.focus()
    }
  }

  moveUp(event) {
    const item = this.itemFor(event)
    const previousItem = item?.previousElementSibling

    if (!item || !previousItem) {
      return
    }

    previousItem.parentElement.insertBefore(item, previousItem)
    this.syncListUi()
    this.syncListHiddenField()
  }

  moveDown(event) {
    const item = this.itemFor(event)
    const nextItem = item?.nextElementSibling

    if (!item || !nextItem) {
      return
    }

    nextItem.parentElement.insertBefore(nextItem, item)
    this.syncListUi()
    this.syncListHiddenField()
  }

  removeValue(event) {
    const item = this.itemFor(event)

    if (!item) {
      return
    }

    item.remove()
    this.syncListUi()
    this.syncListHiddenField()
  }

  updateLabel(event) {
    const item = this.itemFor(event)
    const fieldValue = this.readItem(item)

    fieldValue.label = event.currentTarget.value
    fieldValue.value = event.currentTarget.value

    this.writeItem(item, fieldValue)
    this.syncListHiddenField()
  }

  toggleModifier(event) {
    const item = this.itemFor(event)
    const fieldValue = this.readItem(item)

    fieldValue.modifier = event.currentTarget.checked
      ? this.normalizeModifier(fieldValue.modifier)
      : {}

    this.writeItem(item, fieldValue)
    this.syncModifierUi(item, fieldValue)
    this.syncListHiddenField()
  }

  updateModifier(event) {
    const item = this.itemFor(event)
    const fieldValue = this.readItem(item)

    fieldValue.modifier = this.normalizeModifier(fieldValue.modifier)
    fieldValue.modifier.modifier_value = this.centsFromDisplay(
      event.currentTarget.value
    )

    this.writeItem(item, fieldValue)
    this.syncListHiddenField()
  }

  updateSingleValue() {
    this.hiddenTarget.value = JSON.stringify([
      { value: this.singleInputTarget.value }
    ])
  }

  updateMultiplier() {
    const fieldValues = this.readHiddenValues()
    const fieldValue = fieldValues[0] || {}

    fieldValue.modifier = this.normalizeModifier(
      fieldValue.modifier,
      this.modifierTypeValue
    )
    fieldValue.modifier.modifier_value = this.centsFromDisplay(
      this.multiplierInputTarget.value
    )

    fieldValues[0] = fieldValue
    this.hiddenTarget.value = JSON.stringify(fieldValues)
  }

  syncListUi() {
    this.itemTargets.forEach((item, index) => {
      const position = this.findElementByRole(item, "position")
      const fieldValue = this.readItem(item)

      if (position) {
        position.textContent = index + 1
      }

      this.syncModifierUi(item, fieldValue)
    })

    this.toggleEmptyState()
  }

  syncListHiddenField() {
    const fieldValues = this.itemTargets.map((item, index) => {
      const fieldValue = this.readItem(item)

      fieldValue.position = index
      this.writeItem(item, fieldValue)

      return fieldValue
    })

    this.hiddenTarget.value = JSON.stringify(fieldValues)
    this.toggleEmptyState()
  }

  syncModifierUi(item, fieldValue) {
    const hasModifier = this.hasModifier(fieldValue)
    const toggle = this.findElementByRole(item, "modifier-toggle")
    const container = this.findElementByRole(item, "modifier-container")
    const input = this.findElementByRole(item, "modifier-input")

    if (toggle) {
      toggle.checked = hasModifier
    }

    if (container) {
      container.hidden = !hasModifier
    }

    if (input && hasModifier) {
      input.value = this.displayFromCents(
        this.normalizeModifier(fieldValue.modifier).modifier_value
      )
    }
  }

  toggleEmptyState() {
    if (this.hasEmptyStateTarget) {
      this.emptyStateTarget.hidden = this.itemTargets.length > 0
    }
  }

  readHiddenValues() {
    try {
      const parsedValues = JSON.parse(this.hiddenTarget.value || "[]")
      return Array.isArray(parsedValues) ? parsedValues : []
    } catch (error) {
      return []
    }
  }

  readItem(item) {
    try {
      const fieldValue = JSON.parse(item?.dataset.fieldValue || "{}")
      return fieldValue && typeof fieldValue === "object" && !Array.isArray(fieldValue)
        ? fieldValue
        : {}
    } catch (error) {
      return {}
    }
  }

  writeItem(item, fieldValue) {
    item.dataset.fieldValue = JSON.stringify(fieldValue)
  }

  itemFor(event) {
    return event.currentTarget.closest(
      "[data-form-field-values-editor-target='item']"
    )
  }

  findElementByRole(item, role) {
    return item?.querySelector(
      `[data-form-field-values-editor-role='${role}']`
    )
  }

  hasModifier(fieldValue) {
    return (
      fieldValue &&
      fieldValue.modifier &&
      Object.keys(fieldValue.modifier).length > 0
    )
  }

  normalizeModifier(modifier, modifierType = "fixed_amount") {
    const normalizedModifier =
      modifier && typeof modifier === "object" ? { ...modifier } : {}

    if (!normalizedModifier.modifier_type) {
      normalizedModifier.modifier_type = modifierType
    }

    if (
      normalizedModifier.modifier_value === undefined ||
      normalizedModifier.modifier_value === null ||
      normalizedModifier.modifier_value === ""
    ) {
      normalizedModifier.modifier_value = 0
    }

    return normalizedModifier
  }

  displayFromCents(value) {
    return (Number(value || 0) / 100).toFixed(2)
  }

  centsFromDisplay(value) {
    const parsedValue = Number(value)
    return Number.isFinite(parsedValue) ? Math.round(parsedValue * 100) : 0
  }
}
